// AI Recording Studio Pro — Settings
// Global configuration for the recording studio module
import { useState, useEffect } from "react";
import { RecordingStudioLayout } from "@/components/layout/RecordingStudioLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { motion } from "framer-motion";
import {
  Settings2, Monitor, Mic, Camera, Volume2, Save,
  Palette, Type, Globe, Key, Shield, HardDrive,
  Bell, RefreshCw, Zap, CheckCircle2, Search,
} from "lucide-react";
import { useMagnifierStore } from "@/stores/magnifier-store";
import { usePrivacyStore } from "@/stores/privacy-store";
import { useRecordingStore } from "@/stores/recording-store";
import { studioSettingsAPI } from "@/lib/api";
import { toast } from "sonner";

export default function RecordingSettings() {
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openaiKeySet, setOpenaiKeySet] = useState(false);
  const magnifier = useMagnifierStore();
  const setStoreSettings = useRecordingStore((s) => s.setSettings);
  const setStoreCountdown = useRecordingStore((s) => s.setCountdown);

  // Recording defaults
  const [defaultResolution, setDefaultResolution] = useState("1080p");
  const [defaultFps, setDefaultFps] = useState("30");
  const [defaultCodec, setDefaultCodec] = useState("vp9");
  const [defaultBitrate, setDefaultBitrate] = useState(5000);
  const [autoSave, setAutoSave] = useState(true);
  const [saveFormat, setSaveFormat] = useState("webm");
  const [countdownEnabled, setCountdownEnabled] = useState(true);
  const [countdownSeconds, setCountdownSeconds] = useState(3);

  // Audio
  const [defaultMic, setDefaultMic] = useState(true);
  const [defaultSystemAudio, setDefaultSystemAudio] = useState(true);
  const [noiseSuppression, setNoiseSuppression] = useState(true);
  const [echoCancellation, setEchoCancellation] = useState(true);

  // Webcam
  const [defaultWebcam, setDefaultWebcam] = useState(false);
  const [webcamPosition, setWebcamPosition] = useState("bottom-right");
  const [webcamSize, setWebcamSize] = useState("medium");
  const [webcamShape, setWebcamShape] = useState("circle");

  // AI
  const [aiAutoTranscript, setAiAutoTranscript] = useState(false);
  const [aiAutoChapters, setAiAutoChapters] = useState(false);
  const [aiLanguage, setAiLanguage] = useState("fr-FR");
  const [openaiKey, setOpenaiKey] = useState("");

  // UI
  const [showTips, setShowTips] = useState(true);
  const [compactMode, setCompactMode] = useState(false);
  const [animations, setAnimations] = useState(true);

  // Load persisted settings on mount
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await studioSettingsAPI.get();
        if (!active || !data) return;

        const r = data.recording || {};
        if (r.resolution) setDefaultResolution(r.resolution);
        if (r.fps) setDefaultFps(r.fps);
        if (r.codec) setDefaultCodec(r.codec);
        if (typeof r.bitrate === "number") setDefaultBitrate(r.bitrate);
        if (r.save_format) setSaveFormat(r.save_format);
        if (typeof r.auto_save === "boolean") setAutoSave(r.auto_save);
        if (typeof r.countdown_enabled === "boolean") setCountdownEnabled(r.countdown_enabled);
        if (typeof r.countdown_seconds === "number") setCountdownSeconds(r.countdown_seconds);

        const a = data.audio || {};
        if (typeof a.mic === "boolean") setDefaultMic(a.mic);
        if (typeof a.system_audio === "boolean") setDefaultSystemAudio(a.system_audio);
        if (typeof a.noise_suppression === "boolean") setNoiseSuppression(a.noise_suppression);
        if (typeof a.echo_cancellation === "boolean") setEchoCancellation(a.echo_cancellation);

        const w = data.webcam || {};
        if (typeof w.enabled === "boolean") setDefaultWebcam(w.enabled);
        if (w.position) setWebcamPosition(w.position);
        if (w.size) setWebcamSize(w.size);
        if (w.shape) setWebcamShape(w.shape);

        const ai = data.ai || {};
        if (typeof ai.auto_transcript === "boolean") setAiAutoTranscript(ai.auto_transcript);
        if (typeof ai.auto_chapters === "boolean") setAiAutoChapters(ai.auto_chapters);
        if (ai.language) setAiLanguage(ai.language);

        const ui = data.interface || {};
        if (typeof ui.show_tips === "boolean") setShowTips(ui.show_tips);
        if (typeof ui.compact_mode === "boolean") setCompactMode(ui.compact_mode);
        if (typeof ui.animations === "boolean") setAnimations(ui.animations);

        if (data.magnifier && Object.keys(data.magnifier).length > 0) {
          useMagnifierStore.getState().updateSettings(data.magnifier);
        }
        const p = data.privacy || {};
        if (p.autoPatterns) usePrivacyStore.getState().setAutoPatterns(p.autoPatterns);
        if (p.style) usePrivacyStore.getState().setStyle(p.style);
        setOpenaiKeySet(Boolean(data.openai_key_set));
      } catch {
        toast.error("Impossible de charger les paramètres");
      }
    })();
    return () => { active = false; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const m = useMagnifierStore.getState();

    const payload = {
      recording: {
        resolution: defaultResolution,
        fps: defaultFps,
        codec: defaultCodec,
        bitrate: defaultBitrate,
        save_format: saveFormat,
        auto_save: autoSave,
        countdown_enabled: countdownEnabled,
        countdown_seconds: countdownSeconds,
      },
      audio: {
        mic: defaultMic,
        system_audio: defaultSystemAudio,
        noise_suppression: noiseSuppression,
        echo_cancellation: echoCancellation,
      },
      webcam: {
        enabled: defaultWebcam,
        position: webcamPosition,
        size: webcamSize,
        shape: webcamShape,
      },
      ai: {
        auto_transcript: aiAutoTranscript,
        auto_chapters: aiAutoChapters,
        language: aiLanguage,
      },
      interface: { show_tips: showTips, compact_mode: compactMode, animations },
      magnifier: {
        isEnabled: m.isEnabled,
        shape: m.shape,
        borderColor: m.borderColor,
        size: m.size,
        scale: m.scale,
        halo: m.halo,
        backgroundBlur: m.backgroundBlur,
      },
      privacy: {
        style: usePrivacyStore.getState().style,
        autoPatterns: usePrivacyStore.getState().autoPatterns,
      },
    };

    // Only send the key when one was typed — an empty field must not erase it
    if (openaiKey.trim()) payload.openai_key = openaiKey.trim();

    try {
      const { data } = await studioSettingsAPI.update(payload);
      setOpenaiKeySet(Boolean(data?.openai_key_set));
      setOpenaiKey("");

      // Apply the new defaults to the live studio right away
      setStoreSettings({
        resolution: defaultResolution,
        fps: defaultFps,
        bitrate: defaultBitrate,
        codec: defaultCodec,
        saveFormat: saveFormat,
        includeMic: defaultMic,
        includeSystemAudio: defaultSystemAudio,
        includeWebcam: defaultWebcam,
      });
      setStoreCountdown(countdownEnabled ? countdownSeconds : 0);

      setSaved(true);
      toast.success("Paramètres sauvegardés");
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <RecordingStudioLayout>
      <div className="p-6 md:p-8 space-y-6 max-w-[900px] mx-auto">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, #64748b, #94a3b8)", boxShadow: "0 4px 12px rgba(100,116,139,0.3)" }}>
                  <Settings2 className="h-5 w-5 text-white" />
                </div>
                Paramètres
              </h1>
              <p className="text-sm text-muted-foreground mt-1 ml-[52px]">
                Configuration globale du Recording Studio
              </p>
            </div>
            <Button onClick={handleSave} disabled={saving} className="gap-1.5"
              style={{ background: saved ? "#22c55e" : "linear-gradient(135deg, #6366f1, #8b5cf6)", border: "none" }}>
              {saved ? <CheckCircle2 className="h-4 w-4" /> : <Save className="h-4 w-4" />}
              {saving ? "Sauvegarde..." : saved ? "Sauvegardé !" : "Sauvegarder"}
            </Button>
          </div>
        </motion.div>

        <Tabs defaultValue="recording">
          <TabsList className="grid grid-cols-6 w-full">
            <TabsTrigger value="recording" className="text-xs gap-1"><Monitor className="h-3 w-3" /> Enregistrement</TabsTrigger>
            <TabsTrigger value="audio" className="text-xs gap-1"><Mic className="h-3 w-3" /> Audio</TabsTrigger>
            <TabsTrigger value="webcam" className="text-xs gap-1"><Camera className="h-3 w-3" /> Webcam</TabsTrigger>
            <TabsTrigger value="magnifier" className="text-xs gap-1"><Search className="h-3 w-3" /> Loupe</TabsTrigger>
            <TabsTrigger value="ai" className="text-xs gap-1"><Zap className="h-3 w-3" /> IA</TabsTrigger>
            <TabsTrigger value="interface" className="text-xs gap-1"><Palette className="h-3 w-3" /> Interface</TabsTrigger>
          </TabsList>

          {/* Recording */}
          <TabsContent value="recording" className="mt-6 space-y-4">
            <Card>
              <CardHeader className="pb-3 pt-4 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Monitor className="h-4 w-4 text-primary" /> Paramètres par défaut
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4 px-4 pt-0 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Résolution</label>
                    <Select value={defaultResolution} onValueChange={setDefaultResolution}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="720p" className="text-xs">720p HD</SelectItem>
                        <SelectItem value="1080p" className="text-xs">1080p Full HD</SelectItem>
                        <SelectItem value="2k" className="text-xs">2K QHD</SelectItem>
                        <SelectItem value="4k" className="text-xs">4K UHD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">FPS</label>
                    <Select value={defaultFps} onValueChange={setDefaultFps}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30" className="text-xs">30 FPS</SelectItem>
                        <SelectItem value="60" className="text-xs">60 FPS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Codec</label>
                    <Select value={defaultCodec} onValueChange={setDefaultCodec}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vp8" className="text-xs">VP8</SelectItem>
                        <SelectItem value="vp9" className="text-xs">VP9</SelectItem>
                        <SelectItem value="h264" className="text-xs">H.264</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Format</label>
                    <Select value={saveFormat} onValueChange={setSaveFormat}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="webm" className="text-xs">WebM</SelectItem>
                        <SelectItem value="mp4" className="text-xs">MP4</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Bitrate</label>
                    <span className="text-xs font-mono text-primary">{defaultBitrate} Kbps</span>
                  </div>
                  <Slider value={[defaultBitrate]} onValueChange={(v) => setDefaultBitrate(v[0])}
                    min={500} max={20000} step={500} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-medium">Compte à rebours</span>
                    <p className="text-[10px] text-muted-foreground">Afficher un décompte avant l'enregistrement</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={countdownEnabled} onCheckedChange={setCountdownEnabled} />
                    {countdownEnabled && (
                      <Select value={String(countdownSeconds)} onValueChange={(v) => setCountdownSeconds(parseInt(v))}>
                        <SelectTrigger className="h-7 w-16 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[3, 5, 10].map((s) => <SelectItem key={s} value={String(s)} className="text-xs">{s}s</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-medium">Sauvegarde auto</span>
                    <p className="text-[10px] text-muted-foreground">Sauvegarder automatiquement après l'arrêt</p>
                  </div>
                  <Switch checked={autoSave} onCheckedChange={setAutoSave} />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audio */}
          <TabsContent value="audio" className="mt-6 space-y-4">
            <Card>
              <CardHeader className="pb-3 pt-4 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Mic className="h-4 w-4 text-primary" /> Audio
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4 px-4 pt-0 space-y-3">
                {[
                  { label: "Microphone activé par défaut", desc: "Capturer la voix", value: defaultMic, onChange: setDefaultMic },
                  { label: "Audio système", desc: "Capturer l'audio de l'écran", value: defaultSystemAudio, onChange: setDefaultSystemAudio },
                  { label: "Suppression du bruit", desc: "Réduire le bruit de fond", value: noiseSuppression, onChange: setNoiseSuppression },
                  { label: "Annulation d'écho", desc: "Éviter les retours audio", value: echoCancellation, onChange: setEchoCancellation },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between py-1">
                    <div>
                      <span className="text-xs font-medium">{item.label}</span>
                      <p className="text-[10px] text-muted-foreground">{item.desc}</p>
                    </div>
                    <Switch checked={item.value} onCheckedChange={item.onChange} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Webcam */}
          <TabsContent value="webcam" className="mt-6 space-y-4">
            <Card>
              <CardHeader className="pb-3 pt-4 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Camera className="h-4 w-4 text-primary" /> Webcam
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4 px-4 pt-0 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">Webcam activée par défaut</span>
                  <Switch checked={defaultWebcam} onCheckedChange={setDefaultWebcam} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Position</label>
                    <Select value={webcamPosition} onValueChange={setWebcamPosition}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bottom-right" className="text-xs">Bas droite</SelectItem>
                        <SelectItem value="bottom-left" className="text-xs">Bas gauche</SelectItem>
                        <SelectItem value="top-right" className="text-xs">Haut droite</SelectItem>
                        <SelectItem value="top-left" className="text-xs">Haut gauche</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Taille</label>
                    <Select value={webcamSize} onValueChange={setWebcamSize}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="small" className="text-xs">Petit</SelectItem>
                        <SelectItem value="medium" className="text-xs">Moyen</SelectItem>
                        <SelectItem value="large" className="text-xs">Grand</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Forme</label>
                  <Select value={webcamShape} onValueChange={setWebcamShape}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="circle" className="text-xs">Cercle</SelectItem>
                      <SelectItem value="square" className="text-xs">Carré</SelectItem>
                      <SelectItem value="rounded" className="text-xs">Arrondi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Magnifier */}
          <TabsContent value="magnifier" className="mt-6 space-y-4">
            <Card>
              <CardHeader className="pb-3 pt-4 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Search className="h-4 w-4 text-primary" /> Loupe intelligente (Smart Magnifier)
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4 px-4 pt-0 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-medium">Activer la loupe</span>
                    <p className="text-[10px] text-muted-foreground">Activer/désactiver l'effet de loupe globale (Alt+Z)</p>
                  </div>
                  <Switch checked={magnifier.isEnabled} onCheckedChange={magnifier.setEnabled} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Forme</label>
                    <Select value={magnifier.shape} onValueChange={(v) => magnifier.updateSettings({ shape: v })}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="circle" className="text-xs">Circulaire (cercle)</SelectItem>
                        <SelectItem value="rectangle" className="text-xs">Rectangulaire</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Couleur de la bordure</label>
                    <Select value={magnifier.borderColor} onValueChange={(v) => magnifier.updateSettings({ borderColor: v })}>
                      <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="#22c55e" className="text-xs">Vert</SelectItem>
                        <SelectItem value="#3b82f6" className="text-xs">Bleu</SelectItem>
                        <SelectItem value="#ef4444" className="text-xs">Rouge</SelectItem>
                        <SelectItem value="#f59e0b" className="text-xs">Jaune</SelectItem>
                        <SelectItem value="#a855f7" className="text-xs">Violet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Taille de la loupe</label>
                    <span className="text-xs font-mono text-primary">{magnifier.size} px</span>
                  </div>
                  <Slider
                    value={[magnifier.size]}
                    onValueChange={(v) => magnifier.updateSettings({ size: v[0] })}
                    min={50}
                    max={500}
                    step={10}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Niveau de Zoom</label>
                    <span className="text-xs font-mono text-primary">{magnifier.scale.toFixed(1)}x</span>
                  </div>
                  <Slider
                    value={[magnifier.scale]}
                    onValueChange={(v) => magnifier.updateSettings({ scale: v[0] })}
                    min={1.2}
                    max={10.0}
                    step={0.1}
                  />
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium">Effet halo de lumière</span>
                      <p className="text-[10px] text-muted-foreground">Ombre et lueur diffuse autour de la bordure</p>
                    </div>
                    <Switch checked={magnifier.halo} onCheckedChange={(v) => magnifier.updateSettings({ halo: v })} />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium">Flou de l'arrière-plan</span>
                      <p className="text-[10px] text-muted-foreground">Assombrir le fond en dehors de la loupe</p>
                    </div>
                    <Switch checked={magnifier.backgroundBlur} onCheckedChange={(v) => magnifier.updateSettings({ backgroundBlur: v })} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI */}
          <TabsContent value="ai" className="mt-6 space-y-4">
            <Card>
              <CardHeader className="pb-3 pt-4 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary" /> Intelligence Artificielle
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4 px-4 pt-0 space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-muted-foreground block">Clé API OpenAI</label>
                    {openaiKeySet && (
                      <span className="flex items-center gap-1 text-[10px] text-emerald-500 font-medium">
                        <CheckCircle2 className="h-3 w-3" /> Clé configurée
                      </span>
                    )}
                  </div>
                  <Input type="password" value={openaiKey} onChange={(e) => setOpenaiKey(e.target.value)}
                    placeholder={openaiKeySet ? "•••••••• (laisser vide pour conserver)" : "sk-..."}
                    className="h-9 text-xs font-mono" />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Requise pour la génération IA avancée. Stockée côté serveur et jamais renvoyée au navigateur.
                    Modifiable uniquement par un administrateur.
                  </p>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Langue par défaut</label>
                  <Select value={aiLanguage} onValueChange={setAiLanguage}>
                    <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr-FR" className="text-xs">Français</SelectItem>
                      <SelectItem value="en-US" className="text-xs">Anglais (US)</SelectItem>
                      <SelectItem value="en-GB" className="text-xs">Anglais (UK)</SelectItem>
                      <SelectItem value="ar-SA" className="text-xs">Arabe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium">Transcription automatique</span>
                      <p className="text-[10px] text-muted-foreground">Démarrer la transcription avec l'enregistrement</p>
                    </div>
                    <Switch checked={aiAutoTranscript} onCheckedChange={setAiAutoTranscript} />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium">Chapitres automatiques</span>
                      <p className="text-[10px] text-muted-foreground">Détecter les chapitres via l'IA</p>
                    </div>
                    <Switch checked={aiAutoChapters} onCheckedChange={setAiAutoChapters} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Interface */}
          <TabsContent value="interface" className="mt-6 space-y-4">
            <Card>
              <CardHeader className="pb-3 pt-4 px-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Palette className="h-4 w-4 text-primary" /> Interface
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4 px-4 pt-0 space-y-3">
                {[
                  { label: "Afficher les conseils", desc: "Tips et bonnes pratiques", value: showTips, onChange: setShowTips },
                  { label: "Mode compact", desc: "Réduire l'espacement", value: compactMode, onChange: setCompactMode },
                  { label: "Animations", desc: "Animations et transitions", value: animations, onChange: setAnimations },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between py-1">
                    <div>
                      <span className="text-xs font-medium">{item.label}</span>
                      <p className="text-[10px] text-muted-foreground">{item.desc}</p>
                    </div>
                    <Switch checked={item.value} onCheckedChange={item.onChange} />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </RecordingStudioLayout>
  );
}
